/** Project source identities and the read migration for RFC 0355. */
import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { normalizeDocumentPath } from '../documents/mutations'
import { changeProject, projectString } from '../project/scalars'
import type { ProjectDoc, ProjectSource } from '../project/types'
import { worldProjectMounts } from '../world/reads'
import type { WorldDoc } from '../world/types'
import { vcsCapabilityDocId } from './reads'

export const DEFAULT_SOURCE_ID = 'default'

/** An old mount may be Git even though the project never adopted VCS. */
export function hasInferredDefaultSource(doc: unknown): boolean {
  const project = (doc ?? {}) as Partial<ProjectDoc>
  return project.sources === undefined && !vcsCapabilityDocId(doc)
}

/** The default source is a guess: inferred, or written down still a guess
 *  (RFC 0357 3d). Drawn only where a device has a folder for it. */
export function isAssumedDefaultSource(doc: unknown): boolean {
  if (hasInferredDefaultSource(doc)) return true
  const { defaultSourceId, sources } = projectSources(doc)
  return sources.some((s) => s.id === defaultSourceId && s.assumed === true)
}

/** Check a local mount against shared identity, preserving old Git mounts. */
export function sourceKindForMount(
  doc: unknown,
  sourceId: string,
  mountedGit: boolean,
): 'git' | 'folder' | null {
  const source = projectSources(doc).sources.find((s) => s.id === sourceId)
  if (!source) return null
  if (sourceId === DEFAULT_SOURCE_ID && hasInferredDefaultSource(doc))
    return mountedGit ? 'git' : 'folder'
  return source.kind
}

/** Keep an occupied default source when a CLI attaches another repository. */
export function sourceIdForGitAdoption(
  doc: unknown,
  allocate: () => string,
): string {
  return projectSources(doc).sources.length === 0 ||
    hasInferredDefaultSource(doc)
    ? DEFAULT_SOURCE_ID
    : allocate()
}

/** Older projects acquire the same stable source ID on every device. */
export function projectSources(doc: unknown): {
  defaultSourceId: string
  sources: ProjectSource[]
} {
  const project = (doc ?? {}) as Partial<ProjectDoc>
  const explicit = Object.values(project.sources ?? {}).flatMap((s) => {
    if (!s || s.id == null) return []
    const kind = String(s.kind)
    if (kind !== 'git' && kind !== 'folder') return []
    const id = String(s.id)
    return [
      {
        id,
        name: String(s.name ?? id),
        kind,
        ...(s.vcsDocId ? { vcsDocId: String(s.vcsDocId) } : {}),
        ...(s.remote ? { remote: String(s.remote) } : {}),
        ...(s.folder ? { folder: String(s.folder) } : {}),
        ...(s.assumed === true ? { assumed: true } : {}),
      } as ProjectSource,
    ]
  })
  // An explicit empty map means this project deliberately has no sources.
  // Folder conversion can move its last source to a child project; falling
  // back to the legacy default would resurrect the old checkout here.
  if (project.sources && !explicit.length)
    return { defaultSourceId: '', sources: [] }
  if (explicit.length) {
    const defaultSourceId = explicit.some(
      (s) => s.id === String(project.defaultSourceId),
    )
      ? String(project.defaultSourceId)
      : (explicit[0] as ProjectSource).id
    return { defaultSourceId, sources: explicit }
  }
  const vcsDocId = vcsCapabilityDocId(doc)
  return {
    defaultSourceId: DEFAULT_SOURCE_ID,
    sources: [
      {
        id: DEFAULT_SOURCE_ID,
        name: 'Project files',
        kind: vcsDocId ? 'git' : 'folder',
        ...(vcsDocId ? { vcsDocId } : {}),
      },
    ],
  }
}

/** Materialize the read migration before adding or changing a source. */
export function ensureProjectSources(
  d: ProjectDoc,
  legacyDefaultKind?: 'git' | 'folder',
): void {
  if (d.sources) return
  const legacy = projectSources(d).sources[0] as ProjectSource
  if (legacyDefaultKind && hasInferredDefaultSource(d))
    legacy.kind = legacyDefaultKind
  // Written down, still a guess, unless a git pointer says it is real.
  if (hasInferredDefaultSource(d) && !legacyDefaultKind) legacy.assumed = true
  d.sources = { [DEFAULT_SOURCE_ID]: legacy }
  d.defaultSourceId = DEFAULT_SOURCE_ID
}

export async function addProjectSource(
  repo: Repo,
  projectId: string,
  source: ProjectSource,
  legacyDefaultKind?: 'git' | 'folder',
): Promise<void> {
  if (!source.id || source.id.includes('/'))
    throw new Error('invalid source ID')
  if (source.folder && normalizeDocumentPath(source.folder) !== source.folder)
    throw new Error('invalid source folder')
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  if (source.folder)
    assertSourceFolderAvailable(project.doc(), source.id, source.folder)
  changeProject(project, (d) => {
    const firstDefault =
      source.id === DEFAULT_SOURCE_ID &&
      !Object.keys(d.sources ?? {}).length &&
      !vcsCapabilityDocId(d)
    ensureProjectSources(d, legacyDefaultKind)
    if (d.sources?.[source.id] && !firstDefault)
      throw new Error('source already exists')
    if (source.kind === 'folder' && source.vcsDocId)
      throw new Error('a plain folder cannot have a VCS document')
    d.sources![source.id] = source
  })
}

/** Move the visible file mount without touching this device's checkout. */
export async function setProjectSourceFolder(
  repo: Repo,
  projectId: string,
  sourceId: string,
  folder: string,
  worldId?: string,
): Promise<void> {
  const path = normalizeDocumentPath(folder)
  if (!path) throw new Error('invalid source folder')
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  if (
    !projectSources(project.doc()).sources.some(
      (source) => source.id === sourceId,
    )
  )
    throw new Error('unknown source')
  assertSourceFolderAvailable(project.doc(), sourceId, path)
  if (worldId) {
    const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
    await (world as { whenReady?: () => Promise<void> }).whenReady?.()
    if (
      worldProjectMounts(world.doc()).some(
        (mount) =>
          mount.parentProjectId === projectId &&
          (mount.folder === path ||
            mount.folder.startsWith(`${path}/`) ||
            path.startsWith(`${mount.folder}/`)),
      )
    )
      throw new Error(`${path} overlaps a nested project`)
  }
  changeProject(project, (draft) => {
    ensureProjectSources(draft)
    draft.sources![sourceId]!.folder = path
  })
}

function assertSourceFolderAvailable(
  doc: ProjectDoc,
  sourceId: string,
  folder: string,
): void {
  for (const ref of Object.values(doc.documents ?? {})) {
    const path = normalizeDocumentPath(String(ref.path))
    if (
      path === folder ||
      path?.startsWith(`${folder}/`) ||
      folder.startsWith(`${path}/`)
    )
      throw new Error(`${folder} already holds a document`)
  }
  for (const source of projectSources(doc).sources) {
    if (source.id === sourceId || !source.folder) continue
    if (
      source.folder === folder ||
      source.folder.startsWith(`${folder}/`) ||
      folder.startsWith(`${source.folder}/`)
    )
      throw new Error(`${folder} overlaps source ${source.name}`)
  }
}

export async function detachProjectSource(
  repo: Repo,
  projectId: string,
  sourceId: string,
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  changeProject(project, (d) => {
    ensureProjectSources(d)
    if (!d.sources?.[sourceId]) throw new Error('unknown source')
    if (sourceId === projectString(d.defaultSourceId))
      throw new Error('choose another default source first')
    delete d.sources[sourceId]
  })
}
